import { useReducer, useRef } from 'react';
import { MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateCartDeliveryAddress } from '~/api/product';
import { reverseGeocode } from '~/api/facility';
import {
  EMPTY_ADDRESS_FORM,
  addressToFormState,
  iso3ToCountryName,
  resolvedToFormPatch,
  stateCodeToRegionName,
  type AddressFormState,
} from '~/utils/address';
import { hasCoordinates } from '~/utils/addressFormat';
import type {
  Cart as CartType,
  UpdateCartDeliveryAddressMutation,
} from '~/graphql/graphql';
import { patchCartCaches } from '~/utils/cartCache';
import {
  DeliveryAddressEditor,
  type EditorAnnouncement,
  type EditorMode,
} from './DeliveryAddressEditor';
import { SavedDeliveryAddressCard } from './SavedDeliveryAddressCard';

type CachedCart = {
  id: string;
  deliveryAddress: CartType['deliveryAddress'];
  incotermAddressDestination: string | null;
};

type EditorState = {
  isEditing: boolean;
  mode: EditorMode;
  form: AddressFormState;
  latitude: string;
  longitude: string;
  pickedPoint: boolean;
  pendingLookup: boolean;
  resolveFailed: boolean;
  announcement: EditorAnnouncement;
};

const CLOSED_EDITOR: EditorState = {
  isEditing: false,
  mode: 'address',
  form: EMPTY_ADDRESS_FORM,
  latitude: '',
  longitude: '',
  pickedPoint: false,
  pendingLookup: false,
  resolveFailed: false,
  announcement: null,
};

type EditorAction =
  | {
      type: 'open';
      mode: EditorMode;
      form: AddressFormState;
      latitude: string;
      longitude: string;
      pickedPoint: boolean;
    }
  | { type: 'close' }
  | { type: 'setMode'; mode: EditorMode }
  | { type: 'setForm'; form: AddressFormState }
  | { type: 'setLatitude'; value: string }
  | { type: 'setLongitude'; value: string }
  | { type: 'pick'; latitude: string; longitude: string }
  | { type: 'resolveSuccess'; form: AddressFormState }
  | { type: 'resolveFailure' };

function announce(
  state: EditorState,
  kind: NonNullable<EditorAnnouncement>['kind']
): EditorAnnouncement {
  return { kind, seq: (state.announcement?.seq ?? 0) + 1 };
}

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'open':
      return {
        ...CLOSED_EDITOR,
        isEditing: true,
        mode: action.mode,
        form: action.form,
        latitude: action.latitude,
        longitude: action.longitude,
        pickedPoint: action.pickedPoint,
      };
    case 'close':
      return { ...state, isEditing: false };
    case 'setMode':
      return { ...state, mode: action.mode };
    case 'setForm':
      return {
        ...state,
        form: action.form,
        pickedPoint: false,
        latitude: '',
        longitude: '',
        pendingLookup: false,
        resolveFailed: false,
        announcement: null,
      };
    case 'setLatitude':
      return {
        ...state,
        latitude: action.value,
        pickedPoint: true,
        pendingLookup: false,
        resolveFailed: false,
        announcement: null,
      };
    case 'setLongitude':
      return {
        ...state,
        longitude: action.value,
        pickedPoint: true,
        pendingLookup: false,
        resolveFailed: false,
        announcement: null,
      };
    case 'pick':
      return {
        ...state,
        form: EMPTY_ADDRESS_FORM,
        latitude: action.latitude,
        longitude: action.longitude,
        pickedPoint: true,
        pendingLookup: true,
        resolveFailed: false,
        announcement: announce(state, 'point_updated'),
      };
    case 'resolveSuccess':
      return {
        ...state,
        form: action.form,
        pendingLookup: false,
      };
    case 'resolveFailure':
      return {
        ...state,
        pendingLookup: false,
        resolveFailed: true,
        announcement: announce(state, 'resolve_failed'),
      };
  }
}

type Props = {
  cartId: string;
  deliveryAddress: CartType['deliveryAddress'];
  shippingLocationName: string;
  fallbackCenter?: { latitude: number; longitude: number } | null;
};

export function DeliveryAddressSection({
  cartId,
  deliveryAddress,
  shippingLocationName,
  fallbackCenter = null,
}: Readonly<Props>) {
  const { t } = useTranslation('cart');
  const qc = useQueryClient();
  const updateDeliveryAddress = useUpdateCartDeliveryAddress();
  const [editor, dispatch] = useReducer(editorReducer, CLOSED_EDITOR);
  const {
    isEditing,
    mode,
    form,
    latitude,
    longitude,
    pickedPoint,
    pendingLookup,
    resolveFailed,
    announcement,
  } = editor;
  const lookupSequenceRef = useRef(0);

  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);
  const coordinatesValid =
    latitude.trim() !== '' &&
    longitude.trim() !== '' &&
    Number.isFinite(parsedLatitude) &&
    Number.isFinite(parsedLongitude) &&
    Math.abs(parsedLatitude) <= 90 &&
    Math.abs(parsedLongitude) <= 180;

  const openEditor = () => {
    const hasStoredPoint =
      deliveryAddress != null && hasCoordinates(deliveryAddress);
    const coordsOnly = hasStoredPoint && !deliveryAddress.addressLine1;
    lookupSequenceRef.current += 1;
    dispatch({
      type: 'open',
      mode: coordsOnly ? 'coordinates' : 'address',
      latitude: hasStoredPoint ? String(deliveryAddress.latitude) : '',
      longitude: hasStoredPoint ? String(deliveryAddress.longitude) : '',
      pickedPoint: hasStoredPoint,
      form:
        deliveryAddress && !coordsOnly
          ? addressToFormState(deliveryAddress)
          : EMPTY_ADDRESS_FORM,
    });
  };

  const handlePick = (pickedLatitude: number, pickedLongitude: number) => {
    const sequence = ++lookupSequenceRef.current;
    dispatch({
      type: 'pick',
      latitude: String(Number(pickedLatitude.toFixed(7))),
      longitude: String(Number(pickedLongitude.toFixed(7))),
    });
    reverseGeocode(pickedLatitude, pickedLongitude).then(
      (data) => {
        if (sequence !== lookupSequenceRef.current) return;
        const resolved = data.reverseGeocode;
        if (resolved) {
          dispatch({
            type: 'resolveSuccess',
            form: resolvedToFormPatch(resolved, EMPTY_ADDRESS_FORM),
          });
        } else {
          dispatch({ type: 'resolveFailure' });
        }
      },
      () => {
        if (sequence !== lookupSequenceRef.current) return;
        dispatch({ type: 'resolveFailure' });
      }
    );
  };

  const invalidateLookup = () => {
    lookupSequenceRef.current += 1;
  };

  const onPersistSuccess = (data: UpdateCartDeliveryAddressMutation) => {
    const updated = data.updateCartDeliveryAddress;
    if (!updated) return;
    patchCartCaches(qc, cartId, (cart: CachedCart) => ({
      ...cart,
      deliveryAddress: updated.deliveryAddress ?? null,
      incotermAddressDestination: updated.incotermAddressDestination ?? null,
    }));
    dispatch({ type: 'close' });
  };

  const mutationOptions = {
    onSuccess: onPersistSuccess,
    onError: (error: Error) => toast.error(error.message),
  };

  const persist = (
    deliveryAddress: Parameters<
      typeof updateDeliveryAddress.mutate
    >[0]['deliveryAddress']
  ) => {
    updateDeliveryAddress.mutate({ cartId, deliveryAddress }, mutationOptions);
  };

  const clearAddress = () => persist(null);

  const persistAddress = () => {
    const includeCoordinates = pickedPoint && coordinatesValid;
    persist({
      addressLine1: form.addressLine1.trim() || null,
      addressLine2: form.addressLine2.trim() || null,
      city: form.city.trim() || null,
      region: stateCodeToRegionName(form.country, form.region),
      zipCode: form.zipCode.trim() || null,
      country: form.country ? iso3ToCountryName(form.country) : null,
      ...(includeCoordinates
        ? { latitude: parsedLatitude, longitude: parsedLongitude }
        : {}),
    });
  };

  const persistCoordinates = () =>
    persist({ latitude: parsedLatitude, longitude: parsedLongitude });

  const canSave =
    mode === 'coordinates'
      ? coordinatesValid
      : form.addressLine1.trim() !== '' || (pickedPoint && coordinatesValid);

  const editorPoint =
    pickedPoint && coordinatesValid
      ? { latitude: parsedLatitude, longitude: parsedLongitude }
      : deliveryAddress && hasCoordinates(deliveryAddress)
        ? {
            latitude: deliveryAddress.latitude,
            longitude: deliveryAddress.longitude,
          }
        : { latitude: undefined, longitude: undefined };

  return (
    <div data-testid="delivery-address-section">
      <p className="flex items-center gap-2 text-sm text-neut-700 mb-3 font-medium">
        <MapPin className="w-4 h-4 text-prim-500" />
        {t('cart.delivery_address.title')}
      </p>

      {!isEditing && deliveryAddress && (
        <SavedDeliveryAddressCard
          deliveryAddress={deliveryAddress}
          shippingLocationName={shippingLocationName}
          actionsDisabled={updateDeliveryAddress.isPending}
          onEdit={openEditor}
          onClear={clearAddress}
        />
      )}

      {!isEditing && !deliveryAddress && (
        <button
          type="button"
          data-testid="delivery-address-add"
          className="text-xs font-medium text-prim-700 hover:text-prim-800"
          onClick={openEditor}
        >
          {t('cart.delivery_address.add')}
        </button>
      )}

      {isEditing && (
        <DeliveryAddressEditor
          mode={mode}
          form={form}
          latitude={latitude}
          longitude={longitude}
          coordinatesValid={coordinatesValid}
          pendingLookup={pendingLookup}
          resolveFailed={resolveFailed}
          announcement={announcement}
          mapLatitude={editorPoint.latitude ?? undefined}
          mapLongitude={editorPoint.longitude ?? undefined}
          centerLatitude={fallbackCenter?.latitude}
          centerLongitude={fallbackCenter?.longitude}
          saveDisabled={
            updateDeliveryAddress.isPending || pendingLookup || !canSave
          }
          cancelDisabled={updateDeliveryAddress.isPending}
          onPick={handlePick}
          onModeChange={(next) => dispatch({ type: 'setMode', mode: next })}
          onFormChange={(next) => {
            invalidateLookup();
            dispatch({ type: 'setForm', form: next });
          }}
          onLatitudeChange={(value) => {
            invalidateLookup();
            dispatch({ type: 'setLatitude', value });
          }}
          onLongitudeChange={(value) => {
            invalidateLookup();
            dispatch({ type: 'setLongitude', value });
          }}
          onCancel={() => dispatch({ type: 'close' })}
          onSave={() =>
            mode === 'coordinates' ? persistCoordinates() : persistAddress()
          }
        />
      )}
    </div>
  );
}
